import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/*
  Zadanie optymalizacyjne (podstawa): argument wierzchołka

  Dla f(x) = ax^2 + bx + c:
  - jeśli a > 0, to f ma minimum dla x_w = -b/(2a)
  - jeśli a < 0, to f ma maksimum dla x_w = -b/(2a)

  Losowanie delty w proporcjach 1:3:1 (Δ>0 : Δ=0 : Δ<0),
  choć do samego x_w nie jest to konieczne — ale daje fajną różnorodność.
*/

/* =========================
   Helpers
========================= */

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickByWeight(items) {
  const total = items.reduce((s, it) => s + (it.w ?? 1), 0);
  let r = Math.random() * total;
  for (const it of items) {
    r -= (it.w ?? 1);
    if (r <= 0) return it.v;
  }
  return items[items.length - 1].v;
}

// FractionInput: akceptujemy liczby, ułamki, przecinki
function parseNumber(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (raw === "") return null;

  // ułamek p/q
  const frac = raw.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!Number.isFinite(p) || !Number.isFinite(q) || q === 0) return null;
    return p / q;
  }

  // liczba dziesiętna / całkowita
  if (/^[+\-]?\d+(\.\d+)?$/.test(raw)) {
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

/* =========================
   Ładny zapis wielomianu (bez 1x i -1x)
========================= */

function termAx2(a) {
  if (a === 1) return "x^2";
  if (a === -1) return "-x^2";
  return `${a}x^2`;
}

function termBx(b) {
  if (b === 0) return null;
  const abs = Math.abs(b);
  const core = abs === 1 ? "x" : `${abs}x`;
  return { sign: b > 0 ? "+" : "-", core };
}

function termC(c) {
  if (c === 0) return null;
  return { sign: c > 0 ? "+" : "-", core: String(Math.abs(c)) };
}

/* =========================
   Generator
========================= */

const DELTA_CASES = [
  { v: "POS", w: 1 }, // Δ > 0
  { v: "ZERO", w: 3 }, // Δ = 0
  { v: "NEG", w: 1 }, // Δ < 0
];

export function generateTask() {
  const deltaCase = pickByWeight(DELTA_CASES);

  // a != 0, losujemy też znaki żeby raz było min, raz max
  const aOptions = [-3, -2, -1, 1, 2, 3];
  const a = aOptions[randInt(0, aOptions.length - 1)];

  // chcemy "ładny" wierzchołek: x_w = k (całkowity)
  // b = -2a*k
  const k = randInt(-6, 6) || 2; // żeby nie wyszło 0 zbyt często
  const b = -2 * a * k;

  // chcemy c całkowite i delta w zadanym znaku:
  // Δ = b^2 - 4ac
  // b^2 = 4a^2 k^2
  // weźmy Δ = 4a*t => c = a k^2 - t (zawsze całkowite)
  let t = 0;

  if (deltaCase === "ZERO") {
    t = 0;
  } else {
    const absT = randInt(1, 10);
    // Δ = 4a*t ma mieć odpowiedni znak:
    // - jeśli chcemy Δ>0, to t ma znak a
    // - jeśli chcemy Δ<0, to t ma przeciwny znak do a
    const wantSameSignAsA = deltaCase === "POS";
    const signT = wantSameSignAsA ? Math.sign(a) : -Math.sign(a);
    t = signT * absT;
  }

  const c = a * k * k - t;

  const ask = a > 0 ? "min" : "max";
  const xW = -b / (2 * a); // = k

  return {
    type: "QuadraticExtremeArgTask",
    data: {
      a,
      b,
      c,
      ask,
      xW,
      deltaCase,
      maxPoints: 1,
    },
  };
}

/* =========================
   Render task
========================= */

export function renderTask({ data }) {
  const { a, b, c, ask } = data;

  const bx = termBx(b);
  const cc = termC(c);

  const parts = [termAx2(a)];
  if (bx) parts.push(`${bx.sign} ${bx.core}`);
  if (cc) parts.push(`${cc.sign} ${cc.core}`);

  const polyLatex = parts.join(" ");

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie optymalizacyjne.</strong>
      </p>

      <p>
        Dana jest funkcja <InlineMath math={"f"} /> określona wzorem:
      </p>

      <BlockMath math={`f(x) = ${polyLatex}.`} />

      <p>
        Wyznacz wartość <InlineMath math={"x"} />, dla której funkcja{" "}
        {ask === "min" ? "przyjmuje najmniejszą" : "przyjmuje największą"}{" "}
        wartość.
      </p>
    </div>
  );
}

/* =========================
   Input
========================= */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  return (
    <div style={{ display: "grid", rowGap: 10, maxWidth: 360 }}>
      <div>Podaj argument wierzchołka:</div>
      <FractionInput
        value={ua.x}
        onChange={(v) => onChange({ ...(ua || {}), x: v })}
        disabled={disabled}
        label="x"
      />
    </div>
  );
}

/* =========================
   User answer preview
========================= */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const rawX =
    typeof userAnswer.x === "object"
      ? userAnswer.x?.raw ?? ""
      : String(userAnswer.x ?? "");

  const xLatex = rawX || "\\text{–}";

  return (
    <div>
      <InlineMath math={`x = ${xLatex}`} />
    </div>
  );
}

/* =========================
   Check
========================= */

export function checkAnswer({ data }, userAnswer) {
  const xVal = parseNumber(userAnswer?.x);
  if (xVal == null) return false;
  return Math.abs(xVal - data.xW) < 1e-9;
}

/* =========================
   Answer
========================= */

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <InlineMath math={`x = ${data.xW}`} />
    </div>
  );
}

/* =========================
   Explanation
========================= */

export function renderExplanation({ data }) {
  const { a, b, xW, ask } = data;

  const aWrapped = a < 0 ? `(${a})` : String(a);
  const bWrapped = b < 0 ? `(${b})` : String(b);

  // x_w = -b/(2a) z nawiasami przy ujemnych
  const step1 = String.raw`x_w=-\dfrac{b}{2a}.`;
  const step2 = String.raw`x_w=-\dfrac{${bWrapped}}{2\cdot ${aWrapped}}=${xW}.`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1.</strong>{" "}
        Funkcja kwadratowa ma wierzchołek, a ekstremum (najmniejszą lub największą wartość)
        osiąga właśnie w wierzchołku.
      </div>

      <div style={{ marginBottom: 8 }}>
        {ask === "min" ? (
          <>
            Ponieważ <InlineMath math={"a>0"} />, parabola ma ramiona skierowane w górę,
            więc wierzchołek odpowiada <strong>najmniejszej</strong> wartości funkcji.
          </>
        ) : (
          <>
            Ponieważ <InlineMath math={"a<0"} />, parabola ma ramiona skierowane w dół,
            więc wierzchołek odpowiada <strong>największej</strong> wartości funkcji.
          </>
        )}
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2.</strong> Argument wierzchołka liczymy ze wzoru:
      </div>
      <BlockMath math={step1} />

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 3.</strong> Podstawiamy współczynniki (ujemne liczby bierzemy w nawias):
      </div>
      <BlockMath math={step2} />

      <div style={{ marginTop: 8 }}>
        <strong>Wniosek.</strong>{" "}
        Dla <InlineMath math={`x=${xW}`} /> funkcja{" "}
        {ask === "min" ? "przyjmuje najmniejszą" : "przyjmuje największą"}{" "}
        wartość.
      </div>
    </div>
  );
}


export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
